import { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/ui/Button';
import { Card } from '@/ui/Card';
import { SkeletonTarjetas } from '@/ui/Skeleton';
import { EstadoError } from '@/components/EstadoError';
import { EstadoFiltroVacio, EstadoVacio } from '@/components/EstadoVacio';
import { cn } from '@/lib/cn';
import { NuevoRecurso } from './NuevoRecurso';
import { VistaPrevia } from './VistaPrevia';
import { ORDEN_DE_TIPOS, TIPOS, TarjetaDeRecurso } from './piezas';
import { POR_PAGINA, useRecursos, type Recurso, type TipoDeRecurso } from './api';

/**
 * Biblioteca.tsx — la pantalla de `#biblioteca`. Módulo 7.
 *
 * ## Dos puertas
 *
 *   · `#biblioteca` — el menú lateral (`index.html:320`).
 *   · `#biblioteca/<id>` — **la búsqueda global**, desde el 2026-09-16. Hasta
 *     entonces `search.py` devolvía `"route": "resources"` a secas, así que
 *     buscabas un manual, pulsabas el resultado y aterrizabas en la biblioteca
 *     entera. El id venía en el mismo resultado y se tiraba. Esa segunda puerta
 *     no la ve un `grep` de rutas en el frontend: la ruta la manda el servidor.
 *
 * ## «Nuevo recurso» pregunta el permiso, no el nombre del rol
 *
 *     ${role !== 'colaborador' ? `<button …>Nuevo recurso</button>` : ''}
 *
 * `CLAUDE.md` lo prohíbe por nombre, y fallaba en las dos direcciones contra
 * lo que exige el servidor, `require_permission("recursos", "manage")`: un rol
 * a medida sin ese permiso veía el botón y se comía un 403 al enviar, y un
 * colaborador al que se le concedió no lo veía.
 *
 * ## El filtro por tipo existe para el teclado
 *
 * Los siete chips vanilla eran `<span class="chip">`: medido, **0 de 7**
 * alcanzables con Tab. Ahora son `<button role="radio">` con un solo punto de
 * Tab para el grupo y flechas para recorrerlo, el patrón de
 * `features/notificaciones/Compositor.tsx`. Y la fila se desliza en vez de
 * envolver: a 375 px los chips envueltos ocupaban 144 px de alto antes del
 * primer recurso — la misma decisión que tomó `ui/Tabs`.
 */

type Filtro = TipoDeRecurso | 'todos';

const FILTROS: Array<{ clave: Filtro; etiqueta: string; icono?: string }> = [
  { clave: 'todos', etiqueta: 'Todos' },
  ...ORDEN_DE_TIPOS.map((t) => ({ clave: t, etiqueta: TIPOS[t].plural, icono: TIPOS[t].icono })),
];

/** Minúsculas y sin tildes: «infografia» encuentra «Infografía». */
const normalizar = (s: string) => s.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();

export function Biblioteca() {
  const puedeCrear = Auth.can('recursos', 'manage');
  const { data, isLoading, isError, error, refetch } = useRecursos();

  const [busqueda, setBusqueda] = useState('');
  const [filtro, setFiltro] = useState<Filtro>('todos');
  const [creando, setCreando] = useState(false);
  const [viendo, setViendo] = useState<Recurso | null>(null);
  const refsFiltro = useRef<Array<HTMLButtonElement | null>>([]);

  // La sub-ruta se lee UNA vez, al montar: si se leyera en cada render, cerrar
  // la vista previa la volvería a abrir en el siguiente repintado.
  const [pedido] = useState(() => {
    const p = window._routeParams?.[0];
    return p && /^\d+$/.test(p) ? Number(p) : null;
  });

  useEffect(() => {
    if (pedido == null || !data) return;
    const r = data.find((x) => x.id === pedido);
    if (r) setViendo(r);
    // Un id que no está: el recurso se desactivó, o no entra en los que se
    // piden. Se dice, y la URL vuelve a la biblioteca sin sub-ruta para que
    // recargar no repita el aviso.
    else Utils.toast('Ese recurso ya no está disponible en la biblioteca', 'warning');
  }, [pedido, data]);

  function cerrarVista() {
    setViendo(null);
    if (location.hash !== '#biblioteca') history.replaceState(null, '', '#biblioteca');
  }

  const visibles = useMemo(() => {
    const q = normalizar(busqueda.trim());
    return (data ?? []).filter((r) =>
      (filtro === 'todos' || r.resource_type === filtro) &&
      (!q || normalizar(r.title).includes(q) || normalizar(r.description ?? '').includes(q)),
    );
  }, [data, busqueda, filtro]);

  function tecladoEnFiltros(e: React.KeyboardEvent, i: number) {
    const n = FILTROS.length;
    const siguiente =
      e.key === 'ArrowRight' || e.key === 'ArrowDown' ? i + 1
        : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? i - 1
          : e.key === 'Home' ? 0
            : e.key === 'End' ? n - 1
              : null;
    if (siguiente == null) return;
    e.preventDefault();
    const destino = (siguiente + n) % n;
    const f = FILTROS[destino];
    if (!f) return;
    setFiltro(f.clave);
    const boton = refsFiltro.current[destino];
    boton?.focus();
    // En la fila deslizable, el elegido se trae a la vista.
    boton?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }

  return (
    <>
      <header className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-6 font-bold text-brand-ink">
            <i className="bi bi-journal-bookmark" aria-hidden="true" />
            Biblioteca Corporativa
          </h1>
          <p className="mt-1 text-3 text-muted">Documentos, procedimientos y material institucional</p>
        </div>
        {puedeCrear && (
          <Button onClick={() => setCreando(true)}>
            <i className="bi bi-plus-circle" aria-hidden="true" /> Nuevo recurso
          </Button>
        )}
      </header>

      <div className="mb-5 flex flex-col gap-3">
        {/* Un `<input>` con las clases del sistema, como el Directorio, y no
            `ui/Input`: ese lee su id del contexto de `Field` y LANZA si no lo
            hay. La primera versión lo usaba suelto y la pantalla se quedaba en
            blanco sin ningún mensaje — que es exactamente el síntoma de la
            falta de ErrorBoundary que la ficha del módulo 18 tiene anotada. */}
        <div className="search-bar max-w-md">
          <i className="bi bi-search search-bar-icon" aria-hidden="true" />
          <input
            type="search"
            className="search-input"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Buscar recursos…"
            aria-label="Buscar recursos"
          />
        </div>

        <div
          role="radiogroup"
          aria-label="Filtrar por tipo de recurso"
          className="-mx-1 flex gap-1.5 overflow-x-auto px-1 pb-1 [scrollbar-width:thin]"
        >
          {FILTROS.map((f, i) => {
            const activo = filtro === f.clave;
            return (
              <button
                key={f.clave}
                ref={(el) => { refsFiltro.current[i] = el; }}
                type="button"
                role="radio"
                aria-checked={activo}
                tabIndex={activo ? 0 : -1}
                onClick={() => setFiltro(f.clave)}
                onKeyDown={(e) => tecladoEnFiltros(e, i)}
                className={cn(
                  'inline-flex min-h-9 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-xl border px-3 text-2 font-semibold',
                  'transition-colors focus-visible:focus-anillo pointer-coarse:min-h-11',
                  activo
                    ? 'border-brand-ink bg-brand text-on-brand'
                    : 'border-line bg-surface text-ink-2 hover:border-brand-ink hover:text-brand-ink',
                )}
              >
                {f.icono && <i className={cn('bi', f.icono)} aria-hidden="true" />}
                {f.etiqueta}
              </button>
            );
          })}
        </div>
      </div>

      {isLoading ? (
        <SkeletonTarjetas n={6} />
      ) : isError ? (
        <Card className="p-4">
          <EstadoError err={error} que="la biblioteca" onReintentar={() => refetch()} />
        </Card>
      ) : !data?.length ? (
        <EstadoVacio
          icono="bi-journal-bookmark"
          titulo="La biblioteca está vacía"
          descripcion={puedeCrear
            ? 'Sube el primer manual, plantilla o enlace para el equipo.'
            : 'Todavía no se ha publicado ningún recurso.'}
          accion={puedeCrear ? { texto: 'Nuevo recurso', onClick: () => setCreando(true) } : undefined}
        />
      ) : !visibles.length ? (
        <EstadoFiltroVacio
          total={data.length}
          que="recursos"
          onLimpiar={() => { setBusqueda(''); setFiltro('todos'); }}
        />
      ) : (
        <>
          <p className="sr-only" aria-live="polite">
            {visibles.length} recurso{visibles.length === 1 ? '' : 's'}
          </p>
          <ul className="grid gap-4 [grid-template-columns:repeat(auto-fill,minmax(268px,1fr))]">
            {visibles.map((r) => (
              <li key={r.id} className="flex">
                <TarjetaDeRecurso r={r} onVer={setViendo} />
              </li>
            ))}
          </ul>
          {data.length >= POR_PAGINA && (
            <p className="mt-4 text-2 text-muted">
              Se muestran {POR_PAGINA} recursos; puede haber más.
            </p>
          )}
        </>
      )}

      <VistaPrevia recurso={viendo} onCerrar={cerrarVista} />
      {puedeCrear && <NuevoRecurso abierto={creando} onCerrar={() => setCreando(false)} />}
    </>
  );
}
